// ============================================
// File: src/components/pages/StudentSignupPasswordless.jsx
//
// Passwordless student registration (works on desktop AND mobile browsers —
// the same link serves both, satisfying step 3 of the flow):
//
//   1. Validate invitation token from the SMS link (?token=...)
//   2. Collect: first name, last name, DOB, phone, vehicle option, T&C
//   3. POST /api/students/signup-passwordless  → creates Cognito + records
//   4. Immediately start SMS_OTP sign-in → student enters the code, which
//      only verifies the phone number here (any tokens Cognito issues are
//      discarded — registration does NOT sign the student in)
//   5. Redirect to /student-login → student signs in explicitly with their
//      phone number and a fresh OTP
//
// Route (replaces /signup for new invitations, or run in parallel):
//   <Route path="/signup" element={<StudentSignupPasswordless />} />
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  startStudentLogin,
  confirmStudentOtp,
  studentLogout,
} from '../../services/cognitoAuth';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Students must be at least 17 to register (UK provisional licence minimum
// age). This computes the latest DOB that satisfies that — used both as the
// date input's `max` (blocks picking a too-recent date in the native picker)
// and as a submit-time check (covers typed-in dates and browsers that don't
// enforce `max` on <input type="date">).
const MIN_AGE_YEARS = 17;
function getMaxDateOfBirth() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MIN_AGE_YEARS);
  return d.toISOString().split('T')[0]; // YYYY-MM-DD
}

const pageWrap = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: '#f9fafb',
  padding: '2rem 1rem',
};
const card = {
  maxWidth: '30rem',
  width: '100%',
  padding: '2.5rem 2rem',
  backgroundColor: 'white',
  borderRadius: '1rem',
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
};
const label = { display: 'block', marginTop: '1rem', fontSize: '0.9rem', color: '#374151' };
const input = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  fontSize: '1rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.5rem',
  boxSizing: 'border-box',
  marginTop: '0.35rem',
};
const primaryBtn = (disabled) => ({
  width: '100%',
  marginTop: '1.5rem',
  padding: '0.85rem',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'white',
  background: disabled
    ? '#a5b4fc'
    : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
  border: 'none',
  borderRadius: '0.5rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
});
const errorBox = {
  background: '#fef2f2',
  color: '#b91c1c',
  padding: '0.7rem 0.9rem',
  borderRadius: '0.5rem',
  fontSize: '0.9rem',
  marginTop: '1rem',
};

const StudentSignupPasswordless = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [phase, setPhase] = useState('validating'); // validating | invalid | form | otp
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    vehicleOption: 'Automatic',
    tcAccepted: false,
  });

  const [code, setCode] = useState('');
  const challengeRef = useRef(null);
  const registeredNameRef = useRef('');
  const maxDob = getMaxDateOfBirth();

  // ── 1. Validate the invitation token ──────────────────────────────────
  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setError('No invitation token provided');
      setPhase('invalid');
      return;
    }
    setToken(t);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/students/validate-token/${t}`);
        const data = await res.json();
        if (res.ok && data.success) {
          setInvitationData(data.data);
          // Pre-fill the phone the instructor already has on file
          if (data.data?.phone) {
            setForm((f) => ({ ...f, phone: data.data.phone }));
          }
          setPhase('form');
        } else {
          setError(data.error || 'Invalid invitation token');
          setPhase('invalid');
        }
      } catch {
        setError('Failed to validate invitation link — please try again');
        setPhase('invalid');
      }
    })();
  }, [searchParams]);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  // ── 3+4. Register, then immediately start the OTP verification ────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (form.dateOfBirth > maxDob) {
      setError(`You must be at least ${MIN_AGE_YEARS} years old to register.`);
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/students/signup-passwordless`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...form }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || 'Registration failed');
      }

      registeredNameRef.current = data.data.name;
      // Phone-OTP verification doubles as the first login
      challengeRef.current = await startStudentLogin(data.data.phoneE164);
      setPhase('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await confirmStudentOtp({
        session: challengeRef.current.session,
        username: challengeRef.current.username,
        phoneE164: challengeRef.current.phoneE164,
        code,
      });
      if (result.success) {
        // Phone is verified and Cognito has issued tokens, but we don't want
        // registration to double as a silent sign-in — discard those tokens
        // and send the student to the login page to sign in explicitly.
        studentLogout();
        navigate('/student-login', {
          replace: true,
          state: { justRegistered: true, name: registeredNameRef.current },
        });
      } else if (result.retry) {
        challengeRef.current.session = result.session;
        setCode('');
        setError('That code is not right — please try again.');
      }
    } catch {
      setError(
        'Too many incorrect attempts. Your account was created — sign in ' +
          'from the login page to verify your number.'
      );
    } finally {
      setBusy(false);
    }
  };

  // ── Screens ────────────────────────────────────────────────────────────
  if (phase === 'validating') {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, textAlign: 'center', color: '#6b7280' }}>
          Checking your invitation…
        </div>
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, textAlign: 'center' }}>
          <h2 style={{ color: '#111827' }}>Invitation problem</h2>
          <p style={{ color: '#6b7280' }}>{error}</p>
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            Ask your instructor to send you a new registration link.
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'otp') {
    return (
      <div style={pageWrap}>
        <div style={card}>
          <h2 style={{ margin: 0, color: '#111827', fontSize: '1.3rem' }}>
            Verify your phone
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
            We sent a 6-digit code to{' '}
            {challengeRef.current?.maskedPhone || 'your phone'}. Entering it
            verifies your number and signs you in.
          </p>
          {error && <div style={errorBox}>{error}</div>}
          <form onSubmit={handleVerify}>
            <input
              style={{ ...input, letterSpacing: '0.3em', textAlign: 'center', marginTop: '1rem' }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              placeholder="••••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
            />
            <button
              style={primaryBtn(busy || code.length !== 8)}
              disabled={busy || code.length !== 8}
              type="submit"
            >
              {busy ? 'Verifying…' : 'Verify & finish'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // phase === 'form'
  return (
    <div style={pageWrap}>
      <div style={card}>
        <h2 style={{ margin: 0, color: '#111827', fontSize: '1.4rem' }}>
          Complete your registration
        </h2>
        {invitationData?.instructorName && (
          <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: '0.4rem 0 0' }}>
            You were invited by {invitationData.instructorName}.
          </p>
        )}
        {error && <div style={errorBox}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <label style={label}>
            First name
            <input style={input} value={form.firstName} onChange={set('firstName')} required />
          </label>
          <label style={label}>
            Last name
            <input style={input} value={form.lastName} onChange={set('lastName')} required />
          </label>
          <label style={label}>
            Date of birth
            <input
              style={input}
              type="date"
              value={form.dateOfBirth}
              onChange={set('dateOfBirth')}
              max={maxDob}
              required
            />
            <span style={{ display: 'block', marginTop: '0.3rem', fontSize: '0.78rem', color: '#9ca3af' }}>
              You must be at least {MIN_AGE_YEARS} to register.
            </span>
          </label>
          <label style={label}>
            Mobile number (you will sign in with this)
            <input style={input} type="tel" autoComplete="tel" placeholder="07123 456789" value={form.phone} onChange={set('phone')} required />
          </label>
          <label style={label}>
            Vehicle preference
            <select style={input} value={form.vehicleOption} onChange={set('vehicleOption')}>
              <option value="Automatic">Automatic</option>
              <option value="Manual">Manual</option>
            </select>
          </label>

          <label style={{ ...label, display: 'flex', gap: '0.6rem', alignItems: 'flex-start', marginTop: '1.25rem' }}>
            <input type="checkbox" checked={form.tcAccepted} onChange={set('tcAccepted')} required style={{ marginTop: '0.2rem' }} />
            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              I accept the{' '}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#4f46e5' }}>
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color: '#4f46e5' }}>
                Privacy Policy
              </a>
              .
            </span>
          </label>

          <button style={primaryBtn(busy || !form.tcAccepted)} disabled={busy || !form.tcAccepted} type="submit">
            {busy ? 'Creating your account…' : 'Continue — verify my phone'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StudentSignupPasswordless;